import { EeqCampaignOverridesResponse } from '@appTypes/essential-ui/EeqCampaignOverridesResponse';
import { isDealerOptIn } from '@components/dealsheet/Helpers';
import { getOfferedDealType } from '@services/api/GenericClient';
import { NotificationService } from '@services/notifications/NotificationService';
import { setIsDealsheetReload } from '@store/dealsheet/DealsheetActions';
import { DealsheetState } from '@store/dealsheet/DealsheetReducer';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { DealType } from '../enums/DealType';
import { PackageType } from '../enums/essential-ui/PackageType';
import { NotificationHeaderType, NotificationType } from '../enums/NotificationType';
import { RoutePaths } from '../enums/RoutePaths';
import { ServicePortfolioType } from '../enums/ServicePortfolioType';
import { useDealType, useUserDealer } from '../hooks';
import { ServicePortfolioService } from '../services/portfolio/ServicePortfolio';
import { formatDate } from '../services/utils';
import { groupBy } from '../services/utils/Helpers';
import {
  getMarkType,
  isTransUnionNotification,
  localStorageData,
  setLocalStorage,
  sortNotifications,
} from '../services/utils/NotificationUtils';
import { AppState } from '../store/AppState';
import {
  setLoadNotificationsAction,
  setLoadNotificationsFailAction,
  setLoadNotificationsSuccessAction,
  setUpdateFilteredNotificationsAction,
} from '../store/notifications/NotificationActions';
import { INotificationsState } from '../store/notifications/NotificationReducer';
import {
  setIsCustomerProfileModalOpen,
  setSelectedPortfolioType,
  setSelectedServicePortfolioRecord,
  setServiceCustomerProfileResource,
  setServiceIsServiceOnly,
} from '../store/service-portfolio/ServiceActions';
import {
  CurrentUser,
  NotificationHeaderCountResource,
  NotificationHeaderResource,
  NotificationResource,
} from '../types';
import { DealerState } from '../types/DealerState';
import { ServiceCustomerProfileModalResource } from '../types/service-portfolio/ServiceCustomerProfileModalResource';
import { useRouterService } from './common/UseRouterService';
import { useCheckDevice } from './UseCheckDevice';
import { useHeapTrack } from './UseHeapTrack';

export const useNotification = (): NotificationListItemType => {
  const { storeServices, featureToggle, dealerInfos, transUnionStatus } = useSelector<
    AppState,
    DealerState
  >((dealerState) => dealerState.dealer);

  const { storeCode, isPrimaryUser } = useSelector<AppState, CurrentUser>(
    (currentUser) => currentUser.currentUser,
  );

  const { items } = useSelector<AppState, INotificationsState>(
    (notificationState) => notificationState.notification,
  );
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showReadNts, setShowReadNts] = useState<boolean>(true);
  const [headers, setHeaders] = useState<NotificationHeaderResource[]>([]);
  const [markType, setMarkType] = useState<string>('');
  const [notificationBuckets, setNotificationBuckets] =
    useState<NotificationHeaderCountResource>(null);
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<NotificationHeaderType>(
    NotificationHeaderType.All,
  );
  const dispatch = useDispatch();
  const { isIncentiveAdvantageType, isEssentialType } = useUserDealer();
  const { getDealTypeLabel } = useDealType();
  const hasServicePortfolioEnabled = storeServices && storeServices.hasServicePortfolio;
  const { isMobileDevice } = useCheckDevice();
  const { isDealsheetReload } = useSelector<AppState, DealsheetState>(
    (state) => state.dealsheetModernized,
  );
  const { goToMarketConquestAndOpenCustomerProfileModal } = useRouterService();

  const updateHeaderCounts = (): void => {
    const countsMap = groupBy(
      items.filter((x) => !x.isRead),
      'notificationType',
    );
    const headerCountMap: NotificationHeaderCountResource = {
      ServiceAppointment: (countsMap['ServiceAppointment'] || []).length,
      AutoActivatedBySMm: (countsMap['AutoActivatedBySMm'] || []).length,
      FinalPayment: (countsMap['FinalPayment'] || []).length,
      Birthday: (countsMap['Birthday'] || []).length,
      Anniversary: (countsMap['Anniversary'] || []).length,
      MdlServiceArrival: (countsMap['MdlServiceArrival'] || []).length,
      PrivateIncentive: (countsMap['PrivateIncentive'] || []).length,
      AssignedDeal:
        (countsMap['AssignedMarketConquestDeal'] || []).length +
        (countsMap['AssignedDeal'] || []).length,
      EmailCampaignClick:
        (countsMap['EmailCampaignClick'] || []).length +
        (countsMap['ConquestCampaignClick'] || []).length +
        (countsMap['CampaignQrCodeScan'] || []).length +
        (countsMap['CampaignUrlClick'] || []).length +
        (countsMap['ShowroomUrlQrCodeScan'] || []).length +
        (countsMap['ShowroomUrlClicked'] || []).length,
      Showroom: (countsMap['ShowroomOfferSubmitted'] || []).length,
      WebsiteActivity:
        (countsMap['ShopperActivityWebsiteVisit'] || []).length +
        (countsMap['ShopperActivityVehicleViewed'] || []).length +
        (countsMap['ShopperActivityLeadSubmitted'] || []).length,
    };
    setNotificationBuckets(headerCountMap);
  };

  const updateMarkType = (filter: NotificationHeaderType): void => {
    setMarkType(getMarkType(filter));
  };

  const hasTransUnionNotifications = () => items.some((n) => isTransUnionNotification(n));

  const isPrivateIncentiveApplied = (): boolean => {
    return featureToggle.privateIncentive && !isIncentiveAdvantageType;
  };

  const isServiceToSalesVisible = (): boolean => {
    const brandCodes = dealerInfos.map((d) => d.dealerId.substring(0, 2).toLowerCase());
    return (
      !isMobileDevice &&
      !isEssentialType &&
      !isIncentiveAdvantageType &&
      (transUnionStatus ||
        ['bm', 'mi'].some((b) => brandCodes.includes(b)) ||
        hasTransUnionNotifications())
    );
  };

  const isShopperActivityVisible = (): boolean => {
    return featureToggle?.websiteShopperActivity;
  };

  const updateHeaders = (): void => {
    setHeaders([
      {
        key: NotificationHeaderType.All,
        label: 'All',
        hasIcon: false,
        icon: '',
        GroupTypes: [''],
        isVisible: !isIncentiveAdvantageType,
      },
      {
        key: NotificationHeaderType.ServiceAppointment,
        label: 'Service Appointments',
        hasIcon: true,
        icon: 'score-warranty',
        GroupTypes: [NotificationType.ServiceAppointment],
        isVisible: !isIncentiveAdvantageType,
      },
      {
        key: NotificationHeaderType.AutoActivatedBySMm,
        label: 'Service-to-Sales',
        hasIcon: true,
        icon: 'conquest',
        GroupTypes: [NotificationType.AutoActivatedBySMm],
        isVisible: isServiceToSalesVisible(),
      },
      {
        key: NotificationHeaderType.FinalPayment,
        label: 'Payment Due',
        hasIcon: true,
        icon: 'score-payment',
        GroupTypes: [NotificationType.FinalPayment],
        isVisible: !isIncentiveAdvantageType && !isMobileDevice,
      },
      {
        key: NotificationHeaderType.Birthday,
        label: 'Birthdays',
        hasIcon: true,
        icon: 'birthday',
        GroupTypes: [NotificationType.Birthday],
        isVisible: !isIncentiveAdvantageType && !isMobileDevice,
      },
      {
        key: NotificationHeaderType.Anniversary,
        label: 'Vehicle Anniversary',
        hasIcon: true,
        icon: 'score-product',
        GroupTypes: [NotificationType.Anniversary],
        isVisible: !isIncentiveAdvantageType && !isMobileDevice,
      },
      {
        key: NotificationHeaderType.MdlServiceArrival,
        label: 'Service Arrivals',
        hasIcon: true,
        icon: 'service',
        GroupTypes: [NotificationType.MdlServiceArrival],
        isVisible: !isIncentiveAdvantageType,
      },
      {
        key: NotificationHeaderType.EmailCampaignClick,
        label: 'Campaign Activity',
        hasIcon: true,
        icon: 'campaign',
        GroupTypes: [
          NotificationType.EmailCampaignClick,
          NotificationType.ConquestCampaignClick,
          NotificationType.CampaignQrCodeScan,
          NotificationType.CampaignUrlClick,
          NotificationType.ShowroomUrlQrCodeScan,
          NotificationType.ShowroomUrlClicked,
        ],
        isVisible: true,
      },
      {
        key: NotificationHeaderType.AssignedDeal,
        label: 'Deal Assignments',
        hasIcon: true,
        icon: 'reassign-deal',
        GroupTypes: [NotificationType.AssignedDeal, NotificationType.AssignedMarketConquestDeal],
        isVisible: !isIncentiveAdvantageType,
      },
      {
        key: NotificationHeaderType.PrivateIncentive,
        label: 'Private Incentive',
        hasIcon: true,
        icon: 'private-incentive',
        GroupTypes: [NotificationType.PrivateIncentive],
        isVisible: isPrivateIncentiveApplied() && !isMobileDevice,
      },
      {
        key: NotificationHeaderType.WebsiteActivity,
        label: 'Website Activity',
        hasIcon: true,
        icon: 'visit',
        GroupTypes: [
          NotificationType.ShopperActivityWebsiteVisit,
          NotificationType.ShopperActivityVehicleViewed,
          NotificationType.ShopperActivityLeadSubmitted,
        ],
        isVisible: isShopperActivityVisible() && !isIncentiveAdvantageType,
      },
      {
        key: NotificationHeaderType.Showroom,
        label: 'Showroom',
        hasIcon: true,
        icon: 'showroom',
        GroupTypes: [NotificationType.ShowroomOfferSubmitted],
        isVisible: !isIncentiveAdvantageType,
      },
    ]);
  };

  const onNotificationClick = (item: NotificationResource): void => {
    if (!item.isRead) {
      onRead(item);
    }

    // Set up previous location for the back button to redirect user back to it
    // If user goes to loyalty deal sheet via notification from turn deal sheet
    // Turn deal sheet is set to be their last location for them to return to
    const currentURL = window.location.pathname;
    const urlParts = currentURL.split('/');
    const turnPath = urlParts[1] + '/' + urlParts[2];
    const lastNavigation = JSON.parse(localStorage.getItem('lastNavigation') ?? '');
    if (lastNavigation.state === 'app.turn.details' && turnPath !== 'turn/details') {
      const vin = urlParts[2];
      const dealerId = urlParts[3];
      const offerId = urlParts[5];
      const offerDealType = urlParts[6];
      const turnDealsheetRoute = `/dealsheet/${vin}/${dealerId}/offer/${offerId}/${offerDealType}/bps${location.search}`;
      const turnDealsheetNav = {
        state: 'app.turn.dealsheet',
        route: turnDealsheetRoute,
      };
      localStorage.setItem('lastNavigation', JSON.stringify(turnDealsheetNav));
    }

    if (item.notificationType === 'PrivateIncentive') {
      useHeapTrack('Mastermind - Private Incentive Notifications', {
        StoreCode: storeCode,
        PrivateIncentiveDate: formatDate(item.date, 'MM/DD/YYYY'),
        PrivateIncentiveCount: item.notificationValue,
      });
      const loginEvent = new CustomEvent('privateIncentiveNotificationClicked', {
        detail: { date: item.date, count: item.notificationValue },
      });
      window.dispatchEvent(loginEvent);
      setIsVisible(false);
      navigate(RoutePaths.PortfolioAll);
      return;
    }

    if (item.notificationType === NotificationType.ServiceAppointment) {
      const privateIncentiveType =
        item.isConquest && item.isPrivateIncentiveApplied
          ? 'Service'
          : item.isPrivateIncentiveApplied
            ? 'Loyalty'
            : '';

      if (privateIncentiveType !== '') {
        useHeapTrack(
          'Automated PIL - Service Appointment Notification with ' +
          privateIncentiveType +
          ' Private Incentive',
          {
            StoreCode: storeCode,
            DealerId: item.dealerId,
          },
        );
      }
    }

    if (item.notificationType === NotificationType.AutoActivatedBySMm) {
      const privateIncentiveType =
        item.isConquest && item.isPrivateIncentiveApplied
          ? 'Service'
          : item.isPrivateIncentiveApplied
            ? 'Loyalty'
            : '';

      if (privateIncentiveType !== '') {
        useHeapTrack(
          'Automated PIL - Service-to-Sales Notification with ' +
          privateIncentiveType +
          ' Private Incentive',
          {
            StoreCode: storeCode,
            DealerId: item.dealerId,
          },
        );
      }
    }

    if (item.notificationType === NotificationType.MdlServiceArrival) {
      const privateIncentiveType =
        item.isConquest && item.isPrivateIncentiveApplied
          ? 'Service'
          : item.isPrivateIncentiveApplied
            ? 'Loyalty'
            : '';

      if (privateIncentiveType !== '') {
        useHeapTrack(
          'Automated PIL - Service Arrival Notification with ' +
          privateIncentiveType +
          ' Private Incentive',
          {
            StoreCode: storeCode,
            DealerId: item.dealerId,
          },
        );
      }
    }

    if (
      item.notificationType === NotificationType.AssignedMarketConquestDeal ||
      (item.notificationType === NotificationType.AssignedDeal &&
        item.notificationValue === 'MarketConquest')
    ) {
      if (item.notificationType === 'AssignedDeal' && item.notificationValue === 'MarketConquest') {
        item.leadId = item.vin;
      }

      goToMarketConquestAndOpenCustomerProfileModal(item.leadId, item.dealerId);
      setIsVisible(false);
      return;
    }

    if (
      item.notificationType === NotificationType.ConquestCampaignClick ||
      (item.notificationType === NotificationType.EmailCampaignClick &&
        item.notificationValue === 'MarketConquest')
    ) {
      if (
        item.notificationType === NotificationType.EmailCampaignClick &&
        item.notificationValue === 'MarketConquest'
      ) {
        item.leadId = item.vin;
      }

      goToMarketConquestAndOpenCustomerProfileModal(item.leadId, item.dealerId);
      setIsVisible(false);
      return;
    }

    if (
      item.notificationType === NotificationType.ServiceAppointment &&
      item.bps === null &&
      featureToggle.servicePortfolio &&
      hasServicePortfolioEnabled
    ) {
      goToServicePortfolio(item);
      return;
    }

    if (item.bps != null) {
      goToDealsheet(item, isIncentiveAdvantageType);
    }
  };

  const goToServicePortfolio = (item: NotificationResource) => {
    setIsVisible(false);

    ServicePortfolioService.getServiceRecordType(item.vin, item.dealerId)
      .then((res) => {
        const selectedPortfolioType =
          res === ServicePortfolioType.Drive
            ? ServicePortfolioType.Drive
            : ServicePortfolioType.Appointments;
        dispatch(setSelectedPortfolioType(selectedPortfolioType));

        ServicePortfolioService.getSpecificServiceRecord(
          item.vin,
          item.dealerId,
          selectedPortfolioType,
        ).then((servicePortfolioResources) => {
          const portfolioRecord = servicePortfolioResources.find(
            (i) => new Date(i.arrivedDate).getDate() === new Date(item.appointmentDate).getDate(),
          );

          const customerProfileModalResource: ServiceCustomerProfileModalResource = {
            brandId: item.brandId,
            model: item.model,
            trim: item.trim,
            year: item.year,
            vin: item.vin,
            dealerId: item.dealerId,
            miles: item.mileage?.toLocaleString(),
            isTrimConfirmationRequired: item.isTrimConfirmationRequired,
            serviceDate: new Date(item.appointmentDate),
            trade_TradebookType: portfolioRecord.trade_TradebookType,
            tradeValue: portfolioRecord.tradeValue,
            dealType: DealType[getDealTypeLabel(portfolioRecord.dealType)],
          };

          dispatch(setSelectedServicePortfolioRecord(portfolioRecord));
          dispatch(setServiceIsServiceOnly(portfolioRecord.isServiceOnly));
          dispatch(setServiceCustomerProfileResource(customerProfileModalResource));
          dispatch(setIsCustomerProfileModalOpen(true));
          navigate('/service');
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const goToDealsheet = (item: NotificationResource, isIncentiveAdvantageType: boolean) => {
    setIsVisible(false);
    if (!isIncentiveAdvantageType) {
      if (isDealerOptIn(item.dealerId)) {
        getOfferedDealType(item.dealerId, item.vin).then(
          (response: EeqCampaignOverridesResponse) => {
            const offeredDealType = response?.offeredDealType;
            const urlDealType =
              offeredDealType != null && offeredDealType != ''
                ? offeredDealType.toLowerCase()
                : item.dealType.toLowerCase();
            navigate(`/dealsheet/${item.vin}/${item.dealerId}/offer/1/${urlDealType}/bps`);
            dispatch(setIsDealsheetReload(!isDealsheetReload));
          },
        );
      } else {
        navigate(`/dealsheet/${item.vin}/${item.dealerId}/offer/1/${item.dealType}/bps`);
        dispatch(setIsDealsheetReload(!isDealsheetReload));
      }
    } else {
      navigate(`/essential/dealsheet/${item.closedDealId}/${item.dealerId}`);
    }
  };

  const onRead = (item: NotificationResource) => {
    const updatedItems = items.map((notification) =>
      notification.hash === item.hash
        ? { ...notification, isRead: !notification.isRead }
        : notification,
    );
    dispatch(setLoadNotificationsSuccessAction([...updatedItems]));
  };

  const getMobileFilteredItems = (): NotificationResource[] => {
    const headerGroup = headers.filter((x) => x.isVisible)?.map((y) => y.GroupTypes);
    const headerGroupTypes = [].concat(...headerGroup).filter((x) => x !== '');

    return items.filter((n) => headerGroupTypes.includes(n.notificationType));
  };

  const getFilteredItems = (filter: NotificationHeaderType): NotificationResource[] => {
    if (filter == NotificationHeaderType.All) {
      if (isMobileDevice) {
        return getMobileFilteredItems();
      }
      return items;
    }

    return items.filter((n) =>
      headers.find((x) => x.key == filter)?.GroupTypes.includes(n.notificationType),
    );
  };

  const markAllNotifsRead = (filter: string): void => {
    const groupTypes = filter ? headers.find((x) => x.key == filter).GroupTypes : [];
    const updatedItems = [...items];
    updatedItems.forEach((item: NotificationResource) => {
      if (groupTypes.includes(item.notificationType)) {
        item.isRead = true;
      }
    });
    dispatch(setLoadNotificationsSuccessAction(updatedItems));
  };

  const fetchNotifications = (): void => {
    dispatch(setLoadNotificationsAction());

    NotificationService.fetchNotifications(
      storeServices.hasConquest,
      isPrimaryUser,
      featureToggle,
      storeCode,
      dealerInfos,
      isIncentiveAdvantageType,
      dealerInfos[0]?.packageType === PackageType.BlueberryPlus,
      transUnionStatus,
    )
      .then((result: NotificationResource[]) => {
        const storage = localStorageData()[storeCode] || localStorageData();

        const showReadNotifications = storage.showReadNotifications;

        if (isIncentiveAdvantageType) {
          result = result.filter(
            (n) =>
              n.notificationType == NotificationType.EmailCampaignClick &&
              n.isPrivateIncentiveApplied === true,
          );
          setActiveFilter(NotificationHeaderType.EmailCampaignClick);
        }

        const sortedNotifs = sortNotifications(result, NotificationHeaderType.All);
        dispatch(setLoadNotificationsSuccessAction(sortedNotifs));
        setShowReadNts(showReadNotifications);
      })
      .catch(() => {
        dispatch(setLoadNotificationsFailAction());
      });
  };

  useEffect(() => {
    updateHeaderCounts();
    if (items && items.length) {
      setLocalStorage(items, showReadNts, transUnionStatus, activeFilter, isPrimaryUser, storeCode);
    }
  }, [items]);

  useEffect(() => {
    if (items && items.length) {
      setLocalStorage(items, showReadNts, transUnionStatus, activeFilter, isPrimaryUser, storeCode);
    }
  }, [activeFilter]);

  useEffect(() => {
    updateHeaders();
  }, [featureToggle, dealerInfos, isMobileDevice]);

  useEffect(() => {
    if (headers && headers.length && !isIncentiveAdvantageType) {
      const storage = localStorageData()[storeCode] || localStorageData();

      // searchFilter, is the user's previously selected notification type
      const searchFilter = storage.activeSearchFilter;

      if (!headers.find((h) => h.key === searchFilter)?.isVisible) {
        setActiveFilter(NotificationHeaderType.All);
      } else {
        setActiveFilter(searchFilter);
      }
    }
  }, [headers]);

  useEffect(() => {
    if (storeServices.hasConquest !== undefined) fetchNotifications();
  }, [storeServices.hasConquest]);

  useEffect(() => {
    updateMarkType(activeFilter);
    const filteredItems = sortNotifications(getFilteredItems(activeFilter), activeFilter);
    const unreadFilteredItems = filteredItems.filter((x) => !x.isRead || showReadNts);
    if (
      !isIncentiveAdvantageType &&
      isVisible &&
      activeFilter === NotificationHeaderType.PrivateIncentive
    ) {
      useHeapTrack('Mastermind - Private Incentive Notifications', {
        StoreCode: storeCode,
        PrivateIncentiveTabSelected: true,
      });
    }

    dispatch(setUpdateFilteredNotificationsAction(filteredItems, unreadFilteredItems));
  }, [activeFilter, items, showReadNts]);

  return {
    isVisible,
    onNotificationClick,
    setIsVisible,
    onRead,
    activeFilter,
    setActiveFilter,
    fetchNotifications,
    showReadNts,
    setShowReadNts,
    markAllNotifsRead,
    headers,
    notificationBuckets,
    markType,
    isEssentialType,
    isIncentiveAdvantageType,
    getMobileFilteredItems,
  };
};

type NotificationListItemType = {
  isVisible: boolean;
  onNotificationClick(item: NotificationResource): void;
  setIsVisible(value: boolean): void;
  onRead(item: NotificationResource): void;
  activeFilter: NotificationHeaderType;
  setActiveFilter(val: NotificationHeaderType): void;
  fetchNotifications(): void;
  showReadNts: boolean;
  setShowReadNts(val: boolean): void;
  markAllNotifsRead(filter: string): void;
  headers: NotificationHeaderResource[];
  notificationBuckets: NotificationHeaderCountResource;
  markType: string;
  isEssentialType: boolean;
  isIncentiveAdvantageType: boolean;
  getMobileFilteredItems(): NotificationResource[];
};
